<template>
<div class="td-root" :style="themePageStyle">
  <nav class="td-topbar"><div class="container d-flex align-items-center justify-content-between py-2">
    <div class="d-flex align-items-center" style="gap:12px;">
      <a href="javascript:void(0)" @click="$router.push('/')"><img src="assets/images/flockr_logo.png" alt="Flockr" style="height:34px;" /></a>
      <span class="fw-semibold" style="color:var(--fm-h,#F5F0E8);font-size:15px;">Find Transport</span>
    </div>
    <div class="d-flex gap-2">
      <button class="btn btn-sm btn-outline-warning" @click="$router.push('/transport/dashboard')"><i class="mdi mdi-truck-check me-1"></i>I'm a Transporter</button>
      <button class="btn btn-sm btn-warning" @click="$router.push('/marketplace')"><i class="mdi mdi-storefront-outline me-1"></i>Marketplace</button>
    </div>
  </div></nav>

  <div class="container td-container">
    <!-- Hero -->
    <div class="text-center mb-4">
      <h4 class="fw-bold" style="color:var(--fm-h,#F5F0E8);">🚛 Livestock Transport</h4>
      <p style="color:var(--fm-s,#6B6356);font-size:13px;">NTSA-verified transporters for safe, welfare-compliant livestock movement across Kenya</p>
    </div>

    <!-- Filters -->
    <div class="td-filters mb-4">
      <div class="row g-2">
        <div class="col-md-4">
          <div class="input-group"><span class="input-group-text td-addon"><i class="mdi mdi-magnify"></i></span>
            <input v-model="keyword" class="form-control td-input" placeholder="Search transporter or company..." @keyup.enter="search" /></div>
        </div>
        <div class="col-md-3">
          <select v-model="animalType" class="form-select td-input" @change="search">
            <option value="">All animal types</option>
            <option value="cattle">Cattle</option>
            <option value="goats">Goats</option>
            <option value="sheep">Sheep</option>
            <option value="poultry">Poultry</option>
            <option value="pigs">Pigs</option>
            <option value="camels">Camels</option>
          </select>
        </div>
        <div class="col-md-3">
          <select v-model="maxDistance" class="form-select td-input" @change="search">
            <option value="25">Within 25 km</option>
            <option value="50" selected>Within 50 km</option>
            <option value="100">Within 100 km</option>
            <option value="250">Within 250 km</option>
            <option value="">Any distance</option>
          </select>
        </div>
        <div class="col-md-2">
          <button class="btn btn-warning w-100" @click="search"><i class="mdi mdi-magnify me-1"></i>Search</button>
        </div>
      </div>
    </div>

    <!-- Quick filters -->
    <div class="d-flex gap-2 mb-4 flex-wrap">
      <button class="td-chip" :class="{active: animalType==='cattle'}" @click="animalType=animalType==='cattle'?'':'cattle';search()">🐄 Cattle</button>
      <button class="td-chip" :class="{active: animalType==='goats'}" @click="animalType=animalType==='goats'?'':'goats';search()">🐐 Goats</button>
      <button class="td-chip" :class="{active: animalType==='poultry'}" @click="animalType=animalType==='poultry'?'':'poultry';search()">🐓 Poultry</button>
      <button class="td-chip" :class="{active: ntsaOnly}" @click="ntsaOnly=!ntsaOnly;search()">✅ NTSA Verified</button>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="text-center py-5"><div class="spinner-border text-warning"></div><p class="mt-2" style="color:var(--fm-s,#6B6356);">Finding transporters near you...</p></div>

    <!-- Empty -->
    <div v-else-if="!transporters.length" class="text-center py-5">
      <i class="mdi mdi-truck-remove" style="font-size:64px;color:var(--fm-s,#3A3530);"></i>
      <h5 class="mt-3" style="color:var(--fm-h,#F5F0E8);">No Transporters Found</h5>
      <p style="color:var(--fm-s,#6B6356);">Try broadening your search or increasing the distance range.</p>
    </div>

    <!-- Results -->
    <div v-else>
      <p class="mb-3" style="color:var(--fm-s,#6B6356);font-size:13px;">{{ transporters.length }} transporter{{ transporters.length===1?'':'s' }} found</p>
      <div class="row g-3">
        <div class="col-md-6 col-lg-4" v-for="t in transporters" :key="t.id">
          <div class="td-card" @click="$router.push('/transport/'+t.user_id)">
            <div class="d-flex align-items-center gap-3 mb-3">
              <div class="td-avatar">
                <img v-if="t.avatar_url" :src="t.avatar_url" />
                <span v-else>{{ (t.company_name||t.transporter_name||'T').charAt(0) }}</span>
              </div>
              <div class="flex-fill" style="min-width:0;">
                <div class="td-name">{{ t.company_name || t.transporter_name }}</div>
                <div class="td-owner" v-if="t.company_name && t.transporter_name">Operated by {{ t.transporter_name }}</div>
                <div class="td-badge" v-if="String(t.ntsa_verified)==='1'"><i class="mdi mdi-check-decagram me-1" style="color:#50C878;"></i>NTSA Verified</div>
              </div>
              <div v-if="t.avg_rating" class="text-end">
                <div style="color:#FFD700;font-size:14px;"><i class="mdi mdi-star"></i> {{ Number(t.avg_rating).toFixed(1) }}</div>
                <div style="color:var(--fm-s,#6B6356);font-size:10px;">{{ t.total_reviews || 0 }} reviews</div>
              </div>
            </div>

            <div class="d-flex flex-wrap gap-1 mb-3">
              <span v-for="a in animalTypesOf(t)" :key="a" class="td-tag">{{ a.replace(/_/g,' ') }}</span>
            </div>

            <div class="d-flex gap-3 mb-3" style="font-size:11px;color:var(--fm-m,#A09888);">
              <span v-if="t.max_capacity_large"><i class="mdi mdi-cow me-1"></i>{{ t.max_capacity_large }} cattle</span>
              <span v-if="t.max_capacity_small"><i class="mdi mdi-sheep me-1"></i>{{ t.max_capacity_small }} small stock</span>
              <span v-if="t.max_capacity_poultry"><i class="mdi mdi-egg me-1"></i>{{ t.max_capacity_poultry }} birds</span>
            </div>

            <div class="d-flex justify-content-between align-items-center" style="border-top:1px solid var(--fm-br,#1E1E1E);padding-top:10px;">
              <div>
                <div v-if="t.total_trips" style="color:#BFA164;font-weight:600;font-size:13px;">{{ t.total_trips }} trips</div>
                <div v-if="t.on_time_delivery_pct" style="color:var(--fm-s,#6B6356);font-size:10px;">{{ Number(t.on_time_delivery_pct).toFixed(0) }}% on-time</div>
              </div>
              <div v-if="t.distance_km" style="color:var(--fm-s,#6B6356);font-size:12px;">
                <i class="mdi mdi-map-marker me-1"></i>{{ Number(t.distance_km).toFixed(1) }} km
              </div>
              <div v-else-if="t.service_radius_km" style="color:var(--fm-s,#6B6356);font-size:11px;">
                <i class="mdi mdi-radius-outline me-1"></i>{{ t.service_radius_km }} km radius
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</div>
</template>

<style>
:root{--fm-h:#F5F0E8;--fm-s:#6B6356;--fm-m:#A09888;--fm-bg1:#141414;--fm-bg2:#0F0F0F;--fm-bg3:#1E1E1E;--fm-br:#1E1E1E}
html[data-theme="light"]{--fm-h:#1A1A1A;--fm-s:#777;--fm-m:#555;--fm-bg1:#FFFFFF;--fm-bg2:#F7F5F2;--fm-bg3:#F0EBE4;--fm-br:#E8E2DA}
html[data-theme="light"] body{background:#F7F5F2!important;color:#1A1A1A!important}
html[data-theme="light"] [style*="color:#F5F0E8"]{color:#1A1A1A!important}
html[data-theme="light"] [style*="color:#6B6356"]{color:#777!important}
html[data-theme="light"] [style*="color:#A09888"]{color:#555!important}
html[data-theme="light"] .td-root{background:#F7F5F2!important;color:#1A1A1A!important}
html[data-theme="light"] .td-topbar{background:rgba(255,255,255,.95)!important;border-bottom-color:#E8E2DA!important}
html[data-theme="light"] .td-card{background:#FFFFFF!important;border-color:#E8E2DA!important}
html[data-theme="light"] .td-input,.td-addon{background:#FFFFFF!important;border-color:#E0D8D0!important;color:#1A1A1A!important}
html[data-theme="light"] .td-chip{background:#FFFFFF!important;border-color:#E8E2DA!important;color:#555!important}
html[data-theme="light"] .td-chip.active{background:rgba(191,161,100,.1)!important;border-color:#BFA164!important;color:#BFA164!important}
</style>

<style scoped>
.td-root{background:var(--fm-bg2,#0A0A0A);min-height:100vh;color:var(--fm-h,#F5F0E8)}
.td-topbar{position:sticky;top:0;z-index:100;padding:12px 0;background:var(--fm-bg1,#141414);backdrop-filter:blur(10px);border-bottom:1px solid var(--fm-br,#1E1E1E)}
.td-container{padding-top:80px;padding-bottom:60px;max-width:1100px}
.td-input{background:var(--fm-bg1,#141414)!important;border:1px solid var(--fm-br,#1E1E1E);color:var(--fm-h,#F5F0E8)!important;font-size:13px}
.td-input:focus{border-color:#BFA164;box-shadow:0 0 0 2px rgba(191,161,100,.15)}
.td-addon{background:var(--fm-bg2,#0F0F0F)!important;border-color:var(--fm-br,#1E1E1E)!important;color:var(--fm-s,#6B6356)!important}
.td-chip{padding:6px 14px;border-radius:20px;border:1px solid var(--fm-br,#1E1E1E);background:var(--fm-bg1,#141414);color:var(--fm-m,#A09888);font-size:12px;cursor:pointer;transition:all .2s}
.td-chip:hover{border-color:#BFA164;color:#BFA164}
.td-chip.active{background:rgba(191,161,100,.1);border-color:#BFA164;color:#BFA164;font-weight:600}
.td-card{background:var(--fm-bg1,#141414);border:1px solid var(--fm-br,#1E1E1E);border-radius:12px;padding:20px;cursor:pointer;transition:all .2s}
.td-card:hover{border-color:#BFA164;transform:translateY(-2px);box-shadow:0 8px 24px rgba(0,0,0,.2)}
.td-avatar{width:50px;height:50px;border-radius:50%;background:var(--fm-bg3,#1E1E1E);display:flex;align-items:center;justify-content:center;font-size:18px;font-weight:700;color:#BFA164;overflow:hidden;flex-shrink:0}
.td-avatar img{width:100%;height:100%;object-fit:cover}
.td-name{color:var(--fm-h,#F5F0E8);font-weight:600;font-size:14px}
.td-owner{color:var(--fm-m,#A09888);font-size:11px}
.td-badge{color:var(--fm-s,#6B6356);font-size:10px;margin-top:2px}
.td-tag{font-size:10px;padding:2px 8px;border-radius:6px;font-weight:600;background:rgba(191,161,100,.1);color:#BFA164;text-transform:capitalize}
</style>

<script>
/* eslint-disable */
import { executeJson, flocker } from "@/api";
export default {
  name: "TransportDirectory",
  data(){return{
    darkMode:(document.documentElement.getAttribute('data-theme')||localStorage.getItem('flockr_theme')||'dark')!=='light',
    themeObserver:null,
    loading:false,
    keyword:'',animalType:'',maxDistance:'50',ntsaOnly:false,
    transporters:[],
    userLat:null,userLng:null
  }},
  computed:{
    themePageStyle(){return this.darkMode?'background:#0A0A0A;min-height:100vh;color:#F5F0E8;':'background:#F7F5F2;min-height:100vh;color:#1A1A1A;'}
  },
  mounted(){
    var t=localStorage.getItem('flockr_theme')||'dark';window.__flockrSetTheme(t);
    this.darkMode=t!=='light';
    this.syncTheme();
    this.themeObserver=new MutationObserver(this.syncTheme);
    this.themeObserver.observe(document.documentElement,{attributes:true,attributeFilter:['data-theme']});
    this.getUserLocation();
    this.search();
  },
  beforeUnmount(){if(this.themeObserver)this.themeObserver.disconnect()},
  methods:{
    syncTheme(){this.darkMode=document.documentElement.getAttribute('data-theme')!=='light'},
    getUserLocation(){
      if(navigator.geolocation){
        var self=this;
        navigator.geolocation.getCurrentPosition(
          function(pos){self.userLat=pos.coords.latitude;self.userLng=pos.coords.longitude;self.search()},
          function(){self.search()},{timeout:5000}
        );
      }
    },
    animalTypesOf(t){
      try{
        if(Array.isArray(t.animal_types_served)) return t.animal_types_served;
        if(typeof t.animal_types_served==='string') return JSON.parse(t.animal_types_served||'[]');
      }catch(e){}
      return [];
    },
    async search(){
      this.loading=true;this.transporters=[];
      try{
        var params={function:'searchTransporters'};
        if(this.animalType) params.animalType=this.animalType;
        if(this.userLat){params.latitude=this.userLat;params.longitude=this.userLng}
        var r=await executeJson(params,flocker);
        if(r.data&&r.data.success){
          var list=r.data.data.transporters||[];
          // Client-side filters for things the backend doesn't support yet
          if(this.keyword){
            var q=this.keyword.toLowerCase();
            list=list.filter(function(t){return(t.company_name||'').toLowerCase().indexOf(q)>-1||(t.transporter_name||'').toLowerCase().indexOf(q)>-1});
          }
          if(this.ntsaOnly){
            list=list.filter(function(t){return String(t.ntsa_verified)==='1'});
          }
          if(this.maxDistance && this.userLat){
            var maxD=Number(this.maxDistance);
            list=list.filter(function(t){return !t.distance_km || Number(t.distance_km) <= maxD});
          }
          this.transporters=list;
        }
      }catch(e){console.error(e)}
      this.loading=false;
    }
  }
};
</script>
